import { useEffect } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import {
  isMultiSelectedAtomFamily,
  isOnlyNodeSelectedAtomFamily,
  shouldShowStepIdsAtom,
  zapStepNodesAtomFamily,
  shouldShowAddStepButtonAtom,
  ZapNodeData,
  isViewOnlyModeAtom,
  loadableZapMetadataAsyncAtomFamily,
  ZapMetadata,
  isZapErrorObject,
  hasStreamingProgressStartedAtom,
  areHandlesVisibleAtomFamily,
  NodeType,
  isValidUUID,
  hasSourceNodeAtomFamily,
  hasVisibleAnnotationAtomFamily,
  nodeIdToExplainAtom,
} from "@zapier/canvas-state";
import classNames from "classnames";
import { Node, NodeProps } from "@xyflow/react";
import { DEFAULT_ZAP_NODE_COLOR } from "@zapier/canvas-constants";
import { isRemirrorJSONEmpty } from "@zapier/rich-text-editor";

import { AddStepButton } from "../../../../components/AddStepButton";
import { StepId } from "../../../StepId";
import { getDefaultBackgroundColorForAssetType } from "../../../../utils/getDefaultBackgroundColorForAssetType";
import useUpdateNode from "../../../../hooks/useUpdateNode";
import { HiddenZapStep } from "../../../HiddenZapStep";
import { VerticalAnimatedLine } from "../../../VerticalAnimatedLine";
import { FloatingInlineToolbar } from "../../../FloatingInlineToolbar";
import { Handles } from "../../../Handles";
import { NodeHoverWrapper } from "../../../NodeHoverWrapper";
import { usePreviewContext } from "../../../../context/PreviewProvider";

import { ZapNodeContent } from "./ZapNodeContent";
import { LoadingZapNode } from "./Loader";
import styles from "./ZapNode.module.css";

export function ZapNode(props: NodeProps<Node<ZapNodeData>>) {
  const { data, id, type } = props;
  const backgroundColor = getDefaultBackgroundColorForAssetType(
    "zap",
    data.color, // we need actual color without fallback from getDefaultColorForAssetType
  );
  const { zapId, color = DEFAULT_ZAP_NODE_COLOR } = data;
  const loadableData = useAtomValue(loadableZapMetadataAsyncAtomFamily(zapId));
  const fetchedZap =
    loadableData.state === "hasData" ? loadableData.data : undefined;
  const isLoadingZap = loadableData.state === "loading";
  const isZapNotFound =
    loadableData.state === "hasError" ||
    Boolean(fetchedZap && isZapErrorObject(fetchedZap));
  const zap =
    !fetchedZap || isZapNotFound ? undefined : (fetchedZap as ZapMetadata);

  const { isPreview } = usePreviewContext();
  const hasSourceNode = useAtomValue(hasSourceNodeAtomFamily(id));
  const shouldShowStepIds = useAtomValue(shouldShowStepIdsAtom);
  const isMultiSelected = useAtomValue(isMultiSelectedAtomFamily(id));
  const shouldShowAddStepButton = useAtomValue(shouldShowAddStepButtonAtom);
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);
  const updateNodeAndSave = useUpdateNode();

  const setZapSteps = useSetAtom(zapStepNodesAtomFamily(id));

  const isSelected = useAtomValue(isOnlyNodeSelectedAtomFamily(id));
  const hasVisibleAnnotation = useAtomValue(hasVisibleAnnotationAtomFamily(id));
  const nodeIdToExplain = useAtomValue(nodeIdToExplainAtom);
  const isBeingExplained = nodeIdToExplain === id;

  const isToolbarVisible =
    !isViewOnlyMode && isSelected && isValidUUID(id) && !isBeingExplained;

  const hasStreamingProgressStarted = useAtomValue(
    hasStreamingProgressStartedAtom,
  );

  const areHandlesVisible = useAtomValue(areHandlesVisibleAtomFamily(id));

  useEffect(() => {
    // Need Zap to be defined since that will affect the Zap node height in order to prevent overlaps with Zap steps
    if (zap?.zap_id || isZapNotFound) {
      // Delay so that Zap node height is final before we lay out the Zap steps or else Zap steps will overlap with the Zap node
      setTimeout(setZapSteps, 0);
    }
  }, [setZapSteps, zap?.zap_id, isZapNotFound]);

  if (isLoadingZap) {
    return <LoadingZapNode color={color} backgroundColor={backgroundColor} />;
  }

  const hasNotes = !isRemirrorJSONEmpty(data.notesJson);

  return (
    <>
      {!hasSourceNode && shouldShowAddStepButton && !isPreview && (
        <AddStepButton handleType="target" />
      )}
      {shouldShowAddStepButton && !isPreview && <AddStepButton />}
      <NodeHoverWrapper>
        {shouldShowStepIds && <StepId />}
        {isToolbarVisible && (
          <FloatingInlineToolbar
            color={color}
            actors={data.actors}
            hasNotes={hasNotes}
            hasZap={Boolean(zapId)}
            isZapExpanded={data.isExpanded}
            isZapNotFound={isZapNotFound}
            zap={zap}
            nodeType={type as NodeType}
            assetIds={{
              zapId,
            }}
          />
        )}
        <ZapNodeContent
          {...props}
          data={{ ...data, color }}
          zap={zap}
          isZapNotFound={isZapNotFound}
          onUpdateNode={(updateData) =>
            updateNodeAndSave({ data: updateData }, { shouldSave: true })
          }
          className={classNames({
            [styles.wrapperMultiSelected]: isMultiSelected,
            [styles.wrapperSingleSelected]: isSelected || hasVisibleAnnotation,
          })}
          isViewOnlyMode={isViewOnlyMode}
          backgroundColor={backgroundColor}
        />
        {!hasStreamingProgressStarted && zapId && isZapNotFound && (
          <>
            <VerticalAnimatedLine />
            <HiddenZapStep />
          </>
        )}
        <Handles nodeType="zap" isVisible={areHandlesVisible && !isPreview} />
      </NodeHoverWrapper>
    </>
  );
}
